import React, { useState, useEffect } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { ClockClockwise, Plus, Trash, PencilSimple, Buildings } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { DEPARTMENTS, getDeptLabel } from '@/constants/departments';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DAY_KEYS_AR = ['الإثنين', 'الثلاثاء', 'الأربعاء', 'الخميس', 'الجمعة', 'السبت', 'الأحد'];
const DAY_KEYS_EN = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const computeHours = (start, end) => {
  try {
    const [sh, sm] = start.split(':').map(n => parseInt(n, 10));
    const [eh, em] = end.split(':').map(n => parseInt(n, 10));
    const minutes = (eh * 60 + em) - (sh * 60 + sm);
    return Math.max(0, +(minutes / 60).toFixed(2));
  } catch (e) {
    return 0;
  }
};

const emptyForm = {
  department: '',
  daily_hours: 8,
  start_time: '08:00',
  end_time: '16:00',
  rest_days: [4],
  grace_minutes: 30,
  late_penalty_enabled: true,
  overtime_buffer_minutes: 30,
  half_days: {},  // legacy: { "<day_idx>": hours }
  day_overrides: {},  // { "<day_idx>": { start_time, end_time, hours } }
};

export default function WorkSchedulesSection() {
  const { language, direction } = useLanguage();
  const T = (ar, en) => (language === 'ar' ? ar : en);

  const [schedules, setSchedules] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [globalDefaults, setGlobalDefaults] = useState({ ...emptyForm });
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [showDefaultsDialog, setShowDefaultsDialog] = useState(false);
  const [form, setForm] = useState({ ...emptyForm });
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [s, g, d] = await Promise.all([
        axios.get(`${API}/schedules`, { withCredentials: true }),
        axios.get(`${API}/schedules/global-defaults`, { withCredentials: true }),
        axios.get(`${API}/departments`, { withCredentials: true }),
      ]);
      setSchedules(s.data || []);
      setGlobalDefaults(g.data || { ...emptyForm });
      setDepartments(d.data || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const resolveDeptLabel = (val) => {
    if (!val) return '';
    const live = departments.find(d => d.value === val);
    if (live) return language === 'ar' ? (live.label_ar || val) : (live.label_en || live.label_ar || val);
    // fallback to static constants for legacy entries
    return getDeptLabel(val, language);
  };

  const openAdd = () => {
    setEditing(null);
    setForm({ ...emptyForm });
    setShowDialog(true);
  };

  const openEdit = (sched) => {
    setEditing(sched);
    setForm({
      department: sched.department,
      daily_hours: sched.daily_hours,
      start_time: sched.start_time,
      end_time: sched.end_time,
      rest_days: sched.rest_days || [4],
      grace_minutes: sched.grace_minutes ?? 30,
      late_penalty_enabled: !!sched.late_penalty_enabled,
      overtime_buffer_minutes: sched.overtime_buffer_minutes ?? 30,
      half_days: sched.half_days || {},
      day_overrides: sched.day_overrides || {},
    });
    setShowDialog(true);
  };

  // Toggle a per-day override on/off. When enabled, the day uses the schedule's
  // default start/end times as the seed values — the admin can then edit them.
  const toggleDayOverride = (idx) => {
    setForm(p => {
      const k = String(idx);
      const next = { ...(p.day_overrides || {}) };
      if (k in next) {
        delete next[k];
      } else {
        const sh = p.start_time || '08:00';
        const eh = p.end_time || '16:00';
        next[k] = { start_time: sh, end_time: eh, hours: computeHours(sh, eh) };
      }
      return { ...p, day_overrides: next };
    });
  };

  const updateDayOverrideField = (idx, field, value) => {
    setForm(p => {
      const k = String(idx);
      const existing = (p.day_overrides || {})[k] || {};
      const updated = { ...existing, [field]: value };
      // Recompute hours when either time changes.
      if (field === 'start_time' || field === 'end_time') {
        updated.hours = computeHours(updated.start_time, updated.end_time);
      }
      return {
        ...p,
        day_overrides: { ...(p.day_overrides || {}), [k]: updated },
      };
    });
  };

  // Toggle a day between fixed-time half-day and flexible half-day.
  // Flexible day: employee just needs to clock `target_hours` total during
  // the day — no start/end timing, no late/early penalties.
  const toggleFlexibleDay = (idx) => {
    setForm(p => {
      const k = String(idx);
      const existing = (p.day_overrides || {})[k] || {};
      if (existing.flexible) {
        // Switch back to fixed-time mode using schedule defaults.
        const sh = p.start_time || '08:00';
        const eh = p.end_time || '16:00';
        return {
          ...p,
          day_overrides: {
            ...(p.day_overrides || {}),
            [k]: { start_time: sh, end_time: eh, hours: computeHours(sh, eh) },
          },
        };
      }
      // Switch to flexible mode preserving any prior target hours.
      const target = Number(existing.target_hours || existing.hours || p.daily_hours || 8);
      return {
        ...p,
        day_overrides: {
          ...(p.day_overrides || {}),
          [k]: { flexible: true, target_hours: target, hours: target },
        },
      };
    });
  };

  const updateFlexibleTarget = (idx, value) => {
    setForm(p => {
      const k = String(idx);
      const existing = (p.day_overrides || {})[k] || {};
      const target = Math.max(0, Math.min(24, parseFloat(value) || 0));
      return {
        ...p,
        day_overrides: {
          ...(p.day_overrides || {}),
          [k]: { ...existing, flexible: true, target_hours: target, hours: target },
        },
      };
    });
  };

  const toggleRestDay = (idx) => {
    setForm(p => ({
      ...p,
      rest_days: p.rest_days.includes(idx)
        ? p.rest_days.filter(d => d !== idx)
        : [...p.rest_days, idx],
    }));
  };

  const handleSave = async () => {
    if (!form.department) {
      toast.error(T('يرجى اختيار القسم', 'Please select a department'));
      return;
    }
    if (!form.start_time || !form.end_time) {
      toast.error(T('وقت البداية والنهاية مطلوب', 'Start and end times are required'));
      return;
    }
    setSaving(true);
    try {
      await axios.put(`${API}/schedules/department`, form, { withCredentials: true });
      toast.success(T('تم حفظ جدول القسم بنجاح', 'Department schedule saved'));
      setShowDialog(false);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error');
    } finally { setSaving(false); }
  };

  const handleDelete = async (department) => {
    try {
      await axios.delete(`${API}/schedules/department/${encodeURIComponent(department)}`, { withCredentials: true });
      toast.success(T('تم حذف الجدول', 'Schedule deleted'));
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error');
    }
  };

  const handleSaveDefaults = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/schedules/global-defaults`, globalDefaults, { withCredentials: true });
      toast.success(T('تم حفظ الإعدادات الافتراضية', 'Default schedule saved'));
      setShowDefaultsDialog(false);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error');
    } finally { setSaving(false); }
  };

  const dayLabel = (i) => (language === 'ar' ? DAY_KEYS_AR[i] : DAY_KEYS_EN[i]);

  return (
    <Card className="border-[#C4BFF0]" data-testid="schedules-section">
      <CardHeader>
        <div className="flex items-center justify-between flex-wrap gap-2">
          <CardTitle className="text-[#655dc6] flex items-center gap-2">
            <ClockClockwise size={22} weight="duotone" />
            {T('جداول الدوام (لكل قسم)', 'Work Schedules (Per Department)')}
          </CardTitle>
          <div className="flex gap-2">
            <Button variant="outline" onClick={async () => {
              try {
                const { data } = await axios.post(`${API}/schedules/cleanup-default-customs`, {}, { withCredentials: true });
                toast.success(data.message || T('تم التنظيف', 'Cleaned up'));
                fetchAll();
              } catch (e) {
                toast.error(e.response?.data?.detail || T('تعذّر التنفيذ', 'Failed'));
              }
            }} className="border-orange-300 text-orange-700 hover:bg-orange-50 gap-2" data-testid="cleanup-default-customs-btn"
               title={T('يمسح الجداول الشخصية المكررة فيرث الموظف جدول قسمه', 'Strip default per-employee schedules so they inherit the department schedule')}>
              <ClockClockwise size={16} />
              {T('تنظيف الجداول المكررة', 'Cleanup Duplicates')}
            </Button>
            <Button variant="outline" onClick={() => setShowDefaultsDialog(true)}
              className="border-[#C4BFF0] text-[#655dc6] gap-2" data-testid="open-defaults-btn">
              <ClockClockwise size={16} />
              {T('الإعدادات الافتراضية', 'Default Settings')}
            </Button>
            <Button onClick={openAdd} className="bg-[#655dc6] hover:bg-[#544cb0] text-white gap-2" data-testid="add-schedule-btn">
              <Plus size={16} weight="bold" />
              {T('جدول جديد', 'New Schedule')}
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center justify-center h-32"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#655dc6]" /></div>
        ) : (
          <>
            {/* Active defaults summary */}
            <div className="bg-[#E8E6F5] rounded-lg p-4 mb-4 grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
              <div className="flex flex-col items-center justify-center gap-1">
                <p className="text-[#655dc6]/60 text-xs">{T('ساعات افتراضية', 'Default Hours')}</p>
                <p className="text-[#655dc6] font-bold text-base" dir="ltr">{globalDefaults.daily_hours}h</p>
              </div>
              <div className="flex flex-col items-center justify-center gap-1">
                <p className="text-[#655dc6]/60 text-xs">{T('وقت البداية', 'Start')}</p>
                <p className="text-[#655dc6] font-bold text-base tabular-nums" dir="ltr">{globalDefaults.start_time}</p>
              </div>
              <div className="flex flex-col items-center justify-center gap-1">
                <p className="text-[#655dc6]/60 text-xs">{T('وقت النهاية', 'End')}</p>
                <p className="text-[#655dc6] font-bold text-base tabular-nums" dir="ltr">{globalDefaults.end_time}</p>
              </div>
              <div className="flex flex-col items-center justify-center gap-1">
                <p className="text-[#655dc6]/60 text-xs">{T('فترة السماح', 'Grace Period')}</p>
                <p className="text-[#655dc6] font-bold text-base">{globalDefaults.grace_minutes} {T('دقيقة', 'min')}</p>
              </div>
            </div>

            <div className="overflow-x-auto">
              <Table data-testid="schedules-table">
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-center text-[#655dc6]">{T('القسم', 'Department')}</TableHead>
                    <TableHead className="text-center text-[#655dc6]">{T('الساعات', 'Hours')}</TableHead>
                    <TableHead className="text-center text-[#655dc6]">{T('الفترة', 'Period')}</TableHead>
                    <TableHead className="text-center text-[#655dc6]">{T('أيام الراحة', 'Rest Days')}</TableHead>
                    <TableHead className="text-center text-[#655dc6]">{T('نصف دوام', 'Half Days')}</TableHead>
                    <TableHead className="text-center text-[#655dc6]">{T('السماح', 'Grace')}</TableHead>
                    <TableHead className="text-center text-[#655dc6]">{T('الجزاء', 'Penalty')}</TableHead>
                    <TableHead className="text-center text-[#655dc6]">{T('إجراءات', 'Actions')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {schedules.length === 0 ? (
                    <TableRow><TableCell colSpan={8} className="text-center py-8 text-[#655dc6]/60">
                      {T('لا توجد جداول مخصصة. يطبق الجدول الافتراضي على جميع الموظفين.', 'No custom schedules. The default schedule applies.')}
                    </TableCell></TableRow>
                  ) : schedules.map(s => (
                    <TableRow key={s.department} className="border-[#C4BFF0]" data-testid={`schedule-row-${s.department}`}>
                      <TableCell className="font-medium text-[#655dc6] text-center">
                        <div className="flex items-center gap-2 justify-center">
                          <Buildings size={16} weight="duotone" className="text-[#655dc6]/60" />
                          {resolveDeptLabel(s.department)}
                        </div>
                      </TableCell>
                      <TableCell className="text-center font-bold text-[#655dc6]">{s.daily_hours}</TableCell>
                      <TableCell className="text-center text-[#655dc6]/80">
                        <div className="flex justify-center" dir="ltr">
                          <span>{s.start_time} - {s.end_time}</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-center">
                        <div className="flex flex-wrap gap-1 justify-center">
                          {(s.rest_days || []).map(d => (
                            <Badge key={d} className="bg-[#E8E6F5] text-[#655dc6] text-[10px]">{dayLabel(d)}</Badge>
                          ))}
                        </div>
                      </TableCell>
                      <TableCell className="text-center">
                        <div className="flex flex-wrap gap-1 justify-center">
                          {(() => {
                            const overrides = s.day_overrides || {};
                            const halves = s.half_days || {};
                            // Surface day_overrides first (they're the new, richer form).
                            // Fall back to legacy half_days for older schedules.
                            const entries = Object.keys(overrides).length > 0
                              ? Object.entries(overrides).map(([k, v]) => ({
                                  k,
                                  flexible: !!v.flexible,
                                  label: v.flexible
                                    ? `${dayLabel(Number(k))} · ${T('مرن', 'Flex')} ${v.target_hours ?? v.hours ?? 0}${T('س', 'h')}`
                                    : `${dayLabel(Number(k))} · ${v.start_time}–${v.end_time}`,
                                }))
                              : Object.entries(halves).map(([k, hrs]) => ({ k, flexible: false, label: `${dayLabel(Number(k))} · ${hrs}${T('س', 'h')}` }));
                            if (entries.length === 0) {
                              return <span className="text-[10px] text-[#655dc6]/40">—</span>;
                            }
                            return entries
                              .sort((a, b) => Number(a.k) - Number(b.k))
                              .map((e) => (
                                <Badge key={e.k} className={e.flexible ? 'bg-sky-100 text-sky-700 text-[10px]' : 'bg-amber-100 text-amber-700 text-[10px]'}>
                                  {e.label}
                                </Badge>
                              ));
                          })()}
                        </div>
                      </TableCell>
                      <TableCell className="text-center text-[#655dc6]/80">{s.grace_minutes} {T('د', 'm')}</TableCell>
                      <TableCell className="text-center">
                        <Badge className={s.late_penalty_enabled ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-600'}>
                          {s.late_penalty_enabled ? T('مفعّل', 'On') : T('معطّل', 'Off')}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-center">
                        <div className="flex gap-1 justify-center">
                          <Button variant="ghost" size="icon" className="h-8 w-8 text-[#655dc6] hover:bg-[#E8E6F5]"
                            onClick={() => openEdit(s)} data-testid={`edit-schedule-${s.department}`}>
                            <PencilSimple size={16} />
                          </Button>
                          <Button variant="ghost" size="icon" className="h-8 w-8 text-red-500 hover:bg-red-50"
                            onClick={() => handleDelete(s.department)} data-testid={`delete-schedule-${s.department}`}>
                            <Trash size={16} />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </>
        )}
      </CardContent>

      {/* Add/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="sm:max-w-[520px]" dir={direction} data-testid="schedule-dialog">
          <div dir={direction} style={{ direction }}>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-[#655dc6]">
                <ClockClockwise size={22} weight="duotone" />
                {editing ? T('تعديل جدول القسم', 'Edit Department Schedule') : T('جدول دوام جديد', 'New Department Schedule')}
              </DialogTitle>
              <DialogDescription className="text-xs">
                {T('يطبَّق هذا الجدول على جميع موظفي القسم ما لم يكن للموظف جدول مخصص.',
                   'Applies to all employees in this department unless they have a custom override.')}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label>{T('القسم', 'Department')}</Label>
                <Select value={form.department} onValueChange={v => setForm(p => ({ ...p, department: v }))}
                  disabled={!!editing}>
                  <SelectTrigger data-testid="schedule-dept-select">
                    <SelectValue placeholder={T('اختر القسم', 'Select Department')} />
                  </SelectTrigger>
                  <SelectContent>
                    {departments.map(d => (
                      <SelectItem key={d.value || d.id} value={d.value}>
                        {language === 'ar' ? (d.label_ar || d.value) : (d.label_en || d.label_ar || d.value)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-3 gap-3" dir="ltr">
                <div className="space-y-2">
                  <Label dir={direction}>{T('عدد الساعات', 'Daily Hours')}</Label>
                  <Input type="number" step="0.5" min="1" max="24" dir="ltr" className="text-center"
                    value={form.daily_hours}
                    onChange={e => setForm(p => ({ ...p, daily_hours: parseFloat(e.target.value || 0) }))}
                    data-testid="schedule-hours-input" />
                </div>
                <div className="space-y-2">
                  <Label dir={direction}>{T('من (بداية الدوام)', 'From')}</Label>
                  <Input type="time" lang="en-GB" dir="ltr" className="text-center"
                    value={form.start_time}
                    onChange={e => setForm(p => ({ ...p, start_time: e.target.value }))}
                    data-testid="schedule-start-input" />
                </div>
                <div className="space-y-2">
                  <Label dir={direction}>{T('إلى (نهاية الدوام)', 'To')}</Label>
                  <Input type="time" lang="en-GB" dir="ltr" className="text-center"
                    value={form.end_time}
                    onChange={e => setForm(p => ({ ...p, end_time: e.target.value }))}
                    data-testid="schedule-end-input" />
                </div>
              </div>
              <div className="space-y-2">
                <Label>{T('أيام الراحة', 'Rest Days')}</Label>
                <div className="flex flex-wrap gap-1.5">
                  {[0, 1, 2, 3, 4, 5, 6].map(idx => (
                    <button key={idx} type="button" onClick={() => toggleRestDay(idx)}
                      className={`px-2 py-1 rounded-md text-[11px] font-medium transition-all ${
                        form.rest_days.includes(idx)
                          ? 'bg-[#655dc6] text-white shadow'
                          : 'bg-white border border-[#C4BFF0] text-[#655dc6] hover:bg-[#E8E6F5]'
                      }`}
                      data-testid={`rest-day-${idx}`}>
                      {dayLabel(idx)}
                    </button>
                  ))}
                </div>
              </div>

              {/* Per-day schedule overrides — e.g. Thursday is a half-day
                  with different start/end times. Falls back to the schedule's
                  default times when a day is not overridden. */}
              <div className="space-y-2">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <Label>{T('ساعات عمل مخصصة لكل يوم', 'Per-Day Working Hours')}</Label>
                  <span className="text-[10px] text-[#655dc6]/60">
                    {T('اختر اليوم ثم اضبط وقت البداية والنهاية', 'Pick a day, then set its start/end times')}
                  </span>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {[0, 1, 2, 3, 4, 5, 6].map(idx => {
                    const isOverridden = String(idx) in (form.day_overrides || {});
                    const isRest = form.rest_days.includes(idx);
                    return (
                      <button
                        key={idx}
                        type="button"
                        disabled={isRest}
                        onClick={() => toggleDayOverride(idx)}
                        className={`px-2 py-1 rounded-md text-[11px] font-medium transition-all ${
                          isRest
                            ? 'bg-gray-100 text-gray-400 cursor-not-allowed line-through'
                            : isOverridden
                              ? 'bg-amber-500 text-white shadow'
                              : 'bg-white border border-[#C4BFF0] text-[#655dc6] hover:bg-amber-50'
                        }`}
                        title={isRest ? T('يوم راحة — لا يمكن تخصيصه', 'Rest day') : ''}
                        data-testid={`day-override-toggle-${idx}`}
                      >
                        {dayLabel(idx)}
                      </button>
                    );
                  })}
                </div>
                {Object.keys(form.day_overrides || {}).length > 0 && (
                  <div className="space-y-2 mt-2 p-3 rounded-md bg-amber-50 border border-amber-200">
                    {Object.entries(form.day_overrides || {})
                      .sort(([a], [b]) => Number(a) - Number(b))
                      .map(([k, ov]) => (
                        <div key={k} className="grid grid-cols-12 gap-2 items-center" dir="ltr">
                          <span className="col-span-2 text-[11px] text-[#655dc6] font-medium" dir={direction}>
                            {dayLabel(Number(k))}
                          </span>
                          {ov.flexible ? (
                            <>
                              <div className="col-span-7 flex items-center gap-2">
                                <span className="text-[11px] font-medium text-sky-700 whitespace-nowrap" dir={direction}>
                                  {T('ساعات مستهدفة', 'Target hours')}
                                </span>
                                <Input
                                  type="number" min="0.5" max="24" step="0.5" dir="ltr"
                                  className="h-9 text-sm tabular-nums w-24 text-center"
                                  value={ov.target_hours ?? ov.hours ?? 0}
                                  onChange={(e) => updateFlexibleTarget(Number(k), e.target.value)}
                                  data-testid={`day-override-target-hours-${k}`}
                                />
                                <span className="text-[11px] text-sky-700" dir={direction}>{T('س', 'h')}</span>
                              </div>
                              <button
                                type="button"
                                onClick={() => toggleFlexibleDay(Number(k))}
                                className="col-span-3 px-2 py-1 rounded-md text-[10px] font-semibold bg-sky-500 text-white shadow hover:bg-sky-600"
                                data-testid={`day-override-flex-toggle-${k}`}
                                dir={direction}
                              >
                                {T('مرن — تبديل لأوقات ثابتة', 'Flexible — switch to fixed')}
                              </button>
                            </>
                          ) : (
                            <>
                              <div className="col-span-3">
                                <Input
                                  type="time" dir="ltr" step="60"
                                  className="h-9 text-sm tabular-nums w-full min-w-[110px]"
                                  value={ov.start_time || ''}
                                  onChange={(e) => updateDayOverrideField(Number(k), 'start_time', e.target.value)}
                                  data-testid={`day-override-start-${k}`}
                                />
                              </div>
                              <span className="col-span-1 text-center text-xs text-[#655dc6]/60">→</span>
                              <div className="col-span-3">
                                <Input
                                  type="time" dir="ltr" step="60"
                                  className="h-9 text-sm tabular-nums w-full min-w-[110px]"
                                  value={ov.end_time || ''}
                                  onChange={(e) => updateDayOverrideField(Number(k), 'end_time', e.target.value)}
                                  data-testid={`day-override-end-${k}`}
                                />
                              </div>
                              <span className="col-span-1 text-[11px] text-amber-700 font-semibold text-end" dir={direction}>
                                {(ov.hours ?? 0)}{T('س', 'h')}
                              </span>
                              <button
                                type="button"
                                onClick={() => toggleFlexibleDay(Number(k))}
                                className="col-span-2 px-2 py-1 rounded-md text-[10px] font-semibold border border-sky-300 text-sky-700 hover:bg-sky-100"
                                data-testid={`day-override-flex-toggle-${k}`}
                                dir={direction}
                              >
                                {T('تبديل لـ مرن', 'Make flexible')}
                              </button>
                            </>
                          )}
                        </div>
                      ))}
                    <p className="text-[10px] text-amber-700/70 mt-1" dir={direction}>
                      {T('الأيام المرنة: يكفي تحقيق إجمالي الساعات المستهدفة دون التزام بوقت البدء/الانتهاء (لا تأخير ولا انصراف مبكر).',
                         'Flexible days: employee just needs to reach the target total hours — no start/end timing, no late/early-departure penalties.')}
                    </p>
                  </div>
                )}
              </div>
              <div className="grid grid-cols-2 gap-3 items-end">
                <div className="space-y-2">
                  <Label>{T('فترة السماح (دقيقة)', 'Grace Period (min)')}</Label>
                  <Input type="number" min="0" max="240" dir="ltr" className="text-center"
                    value={form.grace_minutes}
                    onChange={e => setForm(p => ({ ...p, grace_minutes: parseInt(e.target.value || 0) }))}
                    data-testid="schedule-grace-input" />
                </div>
                <div className="space-y-2">
                  <Label>{T('احتساب جزاء التأخير', 'Late Penalty')}</Label>
                  <div className="flex items-center gap-2 h-10 px-3 rounded-md border border-[#C4BFF0] bg-white">
                    <Switch checked={form.late_penalty_enabled}
                      onCheckedChange={v => setForm(p => ({ ...p, late_penalty_enabled: v }))}
                      data-testid="schedule-penalty-switch" />
                    <span className="text-sm text-[#655dc6]">
                      {form.late_penalty_enabled ? T('مفعّل', 'Enabled') : T('معطّل', 'Disabled')}
                    </span>
                  </div>
                </div>
              </div>
              <div className="space-y-2">
                <Label className="flex items-center gap-1">
                  <span>⏱️</span>
                  {T('بافر الإضافي قبل الدوام (دقيقة)', 'Pre-shift Overtime Buffer (min)')}
                </Label>
                <Input type="number" min="0" max="240" dir="ltr" className="text-center"
                  value={form.overtime_buffer_minutes ?? 30}
                  onChange={e => setForm(p => ({ ...p, overtime_buffer_minutes: parseInt(e.target.value || 0) }))}
                  data-testid="schedule-ot-buffer-input" />
                <p className="text-[10px] text-amber-700/80 leading-snug bg-amber-50 border border-amber-200 rounded p-2">
                  {T(
                    'الحضور خلال هذه الفترة قبل بداية الدوام لا يُحسب إضافياً. فقط الدقائق التي تتجاوزها تُعتبر إضافي.',
                    'Arrivals within this window before shift do NOT count as overtime. Only minutes beyond this buffer are eligible.'
                  )}
                </p>
              </div>
              <div className="bg-[#E8E6F5] rounded-lg p-2 text-[10px] text-[#655dc6]/70 leading-snug">
                {T(
                  'بعد انتهاء فترة السماح، يُحسب جزاء = (الراتب الأساسي ÷ 30 ÷ ساعات الدوام ÷ 60) × عدد دقائق التأخير',
                  'After grace, penalty = (basic_salary / 30 / daily_hours / 60) × extra_late_minutes'
                )}
              </div>
            </div>
            <DialogFooter className="mt-4">
              <Button onClick={handleSave} disabled={saving} className="bg-[#655dc6] hover:bg-[#544cb0]" data-testid="save-schedule-btn">
                {saving ? '...' : T('حفظ', 'Save')}
              </Button>
              <Button variant="outline" onClick={() => setShowDialog(false)}>{T('إلغاء', 'Cancel')}</Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>

      {/* Defaults Dialog */}
      <Dialog open={showDefaultsDialog} onOpenChange={setShowDefaultsDialog}>
        <DialogContent className="sm:max-w-[520px]" dir={direction} data-testid="defaults-dialog">
          <div dir={direction} style={{ direction }}>
            <DialogHeader>
              <DialogTitle className="text-[#655dc6]">{T('الإعدادات الافتراضية للنظام', 'System Default Schedule')}</DialogTitle>
              <DialogDescription>
                {T('تطبَّق على الأقسام التي ليس لها جدول مخصص.', 'Applied to departments without a custom schedule.')}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div className="grid grid-cols-3 gap-3" dir="ltr">
                <div className="space-y-2">
                  <Label dir={direction}>{T('عدد الساعات', 'Daily Hours')}</Label>
                  <Input type="number" step="0.5" min="1" max="24" dir="ltr" className="text-center"
                    value={globalDefaults.daily_hours}
                    onChange={e => setGlobalDefaults(p => ({ ...p, daily_hours: parseFloat(e.target.value || 0) }))}
                    data-testid="default-hours" />
                </div>
                <div className="space-y-2">
                  <Label dir={direction}>{T('من (بداية الدوام)', 'From')}</Label>
                  <Input type="time" lang="en-GB" dir="ltr" className="text-center"
                    value={globalDefaults.start_time}
                    onChange={e => setGlobalDefaults(p => ({ ...p, start_time: e.target.value }))}
                    data-testid="default-start" />
                </div>
                <div className="space-y-2">
                  <Label dir={direction}>{T('إلى (نهاية الدوام)', 'To')}</Label>
                  <Input type="time" lang="en-GB" dir="ltr" className="text-center"
                    value={globalDefaults.end_time}
                    onChange={e => setGlobalDefaults(p => ({ ...p, end_time: e.target.value }))}
                    data-testid="default-end" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3 items-end">
                <div className="space-y-2">
                  <Label>{T('فترة السماح (دقيقة)', 'Grace Period (min)')}</Label>
                  <Input type="number" min="0" max="240" dir="ltr" className="text-center"
                    value={globalDefaults.grace_minutes}
                    onChange={e => setGlobalDefaults(p => ({ ...p, grace_minutes: parseInt(e.target.value || 0) }))}
                    data-testid="default-grace" />
                </div>
                <div className="space-y-2">
                  <Label>{T('جزاء التأخير', 'Late Penalty')}</Label>
                  <div className="flex items-center gap-2 h-10 px-3 rounded-md border border-[#C4BFF0] bg-white">
                    <Switch checked={!!globalDefaults.late_penalty_enabled}
                      onCheckedChange={v => setGlobalDefaults(p => ({ ...p, late_penalty_enabled: v }))}
                      data-testid="default-penalty" />
                    <span className="text-sm text-[#655dc6]">
                      {globalDefaults.late_penalty_enabled ? T('مفعّل', 'On') : T('معطّل', 'Off')}
                    </span>
                  </div>
                </div>
              </div>
            </div>
            <DialogFooter className="mt-4">
              <Button onClick={handleSaveDefaults} disabled={saving}
                className="bg-[#655dc6] hover:bg-[#544cb0]" data-testid="save-defaults-btn">
                {saving ? '...' : T('حفظ', 'Save')}
              </Button>
              <Button variant="outline" onClick={() => setShowDefaultsDialog(false)}>{T('إلغاء', 'Cancel')}</Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
